import { ActiveModifiers } from "react-day-picker";
import { assign, createMachine } from "xstate";
import {
  fetchAvailableSlots,
  IAppointment,
  IDate,
  ISlot,
} from "@nju/data-access-portal-appointment";
import { isError } from "@nju/result";

interface IContext {
  installationToken: string;
  availableDates: IDate[];
  selectedDate?: IAppointment;
  selectedDay?: Date;
  availableSlots: ISlot[];
  selectedSlot?: ISlot;
  disabledDays: Date[];
}

type IEvent =
  | {
      type: "SELECT_DAY";
      value: { day: Date; modifiers: ActiveModifiers };
    }
  | {
      type: "SELECT_SLOT";
      value: ISlot;
    }
  | {
      type: "SET_AVAILABLE_SLOTS";
      value: IDate[];
    }
  | {
      type: "ARROW";
      name: "up" | "down";
    }
  | {
      type: "ERROR";
    };

export const calendarMachine = createMachine<IContext, IEvent>(
  {
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    context: {
      installationToken: "",
      availableDates: [],
      availableSlots: [],
      disabledDays: [],
    },
    initial: "loading",
    states: {
      loading: {
        invoke: {
          src: "fetchAvailableSlots",
        },
        on: {
          SET_AVAILABLE_SLOTS: {
            actions: ["setAvailableDates", "setDisabledDays"],
            target: "ready",
          },
          ERROR: "error",
        },
      },
      ready: {
        on: {
          SELECT_DAY: {
            actions: ["setSelectedDay", "setSlots"],
          },
          SELECT_SLOT: { actions: ["setSelectedSlot"] },
          ARROW: { actions: "selectSlotOnArrowEvent" },
        },
      },
      error: {
        entry: "onCalendarError",
        type: "final",
      },
    },
  },
  {
    actions: {
      setSelectedSlot: assign((context, event) => {
        if (event.type === "SELECT_SLOT") {
          return {
            selectedSlot: event.value,
          };
        }

        return context;
      }),
      selectSlotOnArrowEvent: assign((context, event) => {
        const { availableSlots, selectedSlot } = context;

        if (event.type === "ARROW") {
          if (availableSlots.length === 0) {
            return { selectedSlot: undefined };
          }

          if (event.name === "up") {
            if (!selectedSlot || availableSlots.indexOf(selectedSlot) === 0) {
              return {
                selectedSlot: availableSlots[availableSlots.length - 1],
              };
            }

            return {
              selectedSlot:
                availableSlots[availableSlots.indexOf(selectedSlot) - 1],
            };
          }

          if (
            !selectedSlot ||
            availableSlots.indexOf(selectedSlot) === availableSlots.length - 1
          ) {
            return { selectedSlot: availableSlots[0] };
          }

          return {
            selectedSlot:
              availableSlots[availableSlots.indexOf(selectedSlot) + 1],
          };
        }

        return context;
      }),
      setSelectedDay: assign((context, event) => {
        if (event.type === "SELECT_DAY") {
          return {
            selectedDay: event.value.modifiers.selected
              ? undefined
              : event.value.day,
          };
        }

        return context;
      }),
      setSlots: assign(({ availableDates, selectedDay }) => {
        const slots =
          (selectedDay &&
            availableDates.find((availableDate) => {
              return (
                new Date(availableDate.date).toLocaleDateString() ===
                selectedDay.toLocaleDateString()
              );
            })?.slots) ||
          [];

        return {
          availableSlots: slots,
        };
      }),
      setAvailableDates: assign((context, event) => {
        if (event.type === "SET_AVAILABLE_SLOTS") {
          if (!context.selectedDate || !context.selectedDate.slot) {
            return {
              availableDates: event.value,
            };
          }

          const date = new Date(context.selectedDate.slot.startDate);

          const slots = event.value.find((availableDate) => {
            return (
              new Date(availableDate.date).toLocaleDateString() ===
              date.toLocaleDateString()
            );
          })?.slots;

          return {
            availableDates: event.value,
            selectedDay: date,
            availableSlots: slots || [],
            selectedSlot: context.selectedDate.slot,
          };
        }

        return context;
      }),
      setDisabledDays: assign((context) => {
        const daysWithEmptySlots = context.availableDates
          .filter((date) => {
            return date.slots.length === 0;
          })
          .map((date) => {
            return new Date(date.date);
          });

        return { disabledDays: daysWithEmptySlots };
      }),
    },
    services: {
      fetchAvailableSlots: (context) => {
        return async (callback) => {
          const result = await fetchAvailableSlots({
            installationToken: context.installationToken,
          });

          if (isError(result)) {
            return callback({ type: "ERROR" });
          }

          return callback({ type: "SET_AVAILABLE_SLOTS", value: result.value });
        };
      },
    },
  },
);
